/**
 * Input Component - Design System
 * Features: validation inline, icons, helper text, error states
 */

import React, { forwardRef } from 'react';
import { Check, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  helperText?: string;
  error?: string;
  isValid?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helperText,
      error,
      isValid,
      leftIcon,
      rightIcon,
      className,
      id,
      required,
      disabled,
      ...props
    },
    ref
  ) => {
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    const hasError = !!error;
    const showValidIcon = isValid && !hasError && !disabled;

    return (
      <div className="w-full">
        {/* Label */}
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        {/* Input container */}
        <div className="relative">
          {/* Left icon */}
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-400 w-5 h-5">{leftIcon}</span>
            </div>
          )}

          {/* Input field */}
          <input
            ref={ref}
            id={inputId}
            disabled={disabled}
            className={cn(
              // Base styles
              'w-full px-4 py-2.5 text-base',
              'border rounded-md',
              'transition-all duration-200',
              'placeholder:text-gray-400',

              // Focus styles
              'focus:outline-none focus:ring-2 focus:ring-offset-0',

              // Left icon padding
              leftIcon && 'pl-10',

              // Right icon padding (validation or custom)
              (showValidIcon || rightIcon || hasError) && 'pr-10',

              // State styles
              hasError
                ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500',

              // Disabled styles
              disabled && 'bg-gray-50 cursor-not-allowed opacity-60',

              // Custom className
              className
            )}
            {...props}
          />

          {/* Right icon - Validation or custom */}
          {(showValidIcon || rightIcon || hasError) && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              {hasError ? (
                <AlertCircle className="w-5 h-5 text-red-500" />
              ) : showValidIcon ? (
                <Check className="w-5 h-5 text-green-500" />
              ) : (
                rightIcon && <span className="text-gray-400 w-5 h-5">{rightIcon}</span>
              )}
            </div>
          )}
        </div>

        {/* Helper text or error message */}
        {(helperText || error) && (
          <p
            className={cn(
              'mt-1.5 text-sm',
              hasError ? 'text-red-600' : 'text-gray-500'
            )}
          >
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
